import { Descriptions, Row, Tooltip } from "antd";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { useTranslation } from "react-i18next";
import { FormatData } from "utils/FormatData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { userInfoStore } from "Stores";

interface InformationTabProps {
  data: API_AccessRightsInfo;
}

export const InformationTab = ({ data }: InformationTabProps) => {
  if (!data) {
    return null;
  }
  const { t } = useTranslation(I18N_NS.ADMIN);
  const userStats = data.projectStatistics?.userStatistics;
  const fileStats = data.projectStatistics?.fileStatistics;
  const storageStats = data.projectStatistics?.storageStatistics;
  const dirStats = data.projectStatistics?.directoriesStatistics;
  const aclStats = data.projectStatistics?.accessRightsStatistics;
  const wfStats = data.projectStatistics?.workflowStatistics;
  const mailStats = data.projectStatistics?.mailStatistics;
  const sysAdmin = userInfoStore.role === "ADMIN";

  return (
    <>
      <Row justify="space-between" align="middle">
        <h1>{t("Statistic project information")}</h1>
      </Row>
      <Descriptions bordered title={t("Users (without system administrators)")}>
        <Descriptions.Item label={t("Assigned users")} style={{ marginTop: 20 }}>
          {userStats.attachedUserCount}
          <Tooltip
            title={
              <div>
                <div>
                  <span>{t("Active users: ")}</span>
                  <span className="mint">{userStats.userCount}</span>
                </div>
                <div>
                  <span>{t("Passive users: ")}</span>
                  <span className="mint">{userStats.passiveUserCount}</span>
                </div>
                <div>
                  <span>{t("Hidden Users: ")}</span>
                  <span className="mint">{userStats.hiddenUserCount}</span>
                </div>
                <div>
                  <span>{t("Admins: ")}</span>
                  <span className="mint">{userStats.attachedAdminsCount}</span>
                </div>
              </div>
            }
          >
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ color: "45bfb1", marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
        <Descriptions.Item label={t("All users who opened the project at least one time")}>
          {userStats.activeUserCount}
        </Descriptions.Item>
        {sysAdmin ? (
          <Descriptions.Item label={t("Non billable users")}>{userStats.nonBillableUserCount}</Descriptions.Item>
        ) : null}
        {sysAdmin ? (
          <Descriptions.Item label={t("Last access to project")}>
            {dayjs(userStats.lastAction).format("lll")}
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      <Descriptions
        bordered
        title={
          <>
            {t("Files")} ({t("counted across all modules unless otherwise stated")})
          </>
        }
        style={{ marginTop: 20 }}
      >
        <Descriptions.Item label={t("Latest file upload time")}>
          {dayjs(fileStats.lastFileUpdate).format("lll")}
        </Descriptions.Item>
        <Descriptions.Item label={t("Active files (Module Documents/Views)")}>
          {fileStats.activeFilesInDocumentsCount}
        </Descriptions.Item>
        <Descriptions.Item label={t("Active files (in all modules)")}>
          {fileStats.activeFilesAllModulesCount}
        </Descriptions.Item>
        <Descriptions.Item label={t("Archived file versions")}>{fileStats.archivedFilesCount}</Descriptions.Item>
        <Descriptions.Item label={t("Symbolic links to files")}>{fileStats.symlinksForFilesCount}</Descriptions.Item>
        {sysAdmin ? (
          <Descriptions.Item label={t("Symbolic links to zero byte files")}>
            {fileStats.symLinksToZeroByteFiles}
            <Tooltip title={t("Won't be included in export!")}>
              <FontAwesomeIcon
                fixedWidth={true}
                size="1x"
                className={"text-error"}
                style={{ marginLeft: 5 }}
                icon={["far", "ban"]}
              />
            </Tooltip>
          </Descriptions.Item>
        ) : null}
        {sysAdmin ? (
          <Descriptions.Item label={t("Virus files")}>
            {fileStats.virusFiles}
            <Tooltip title={t("Won't be included in export!")}>
              <FontAwesomeIcon
                fixedWidth={true}
                size="1x"
                className={"text-error"}
                style={{ marginLeft: 5 }}
                icon={["far", "ban"]}
              />
            </Tooltip>
          </Descriptions.Item>
        ) : null}
        {sysAdmin ? (
          <>
            <Descriptions.Item label={t("0 Byte files")}>
              {fileStats.zeroByteFiles}
              <Tooltip title={t("Won't be included in export!")}>
                <FontAwesomeIcon
                  fixedWidth={true}
                  size="1x"
                  className={"text-error"}
                  style={{ marginLeft: 5 }}
                  icon={["far", "ban"]}
                />
              </Tooltip>
            </Descriptions.Item>

            <Descriptions.Item label={t("0 Byte files (not deleted)")}>
              {fileStats.zeroByteFilesNotDeleted}
              <Tooltip title={t("Won't be included in export!")}>
                <FontAwesomeIcon
                  fixedWidth={true}
                  size="1x"
                  className={"text-error"}
                  style={{ marginLeft: 5 }}
                  icon={["far", "ban"]}
                />
              </Tooltip>
            </Descriptions.Item>

            <Descriptions.Item label={t("0 Byte files (deleted)")}>
              {fileStats.zeroByteFilesDeleted}
              <Tooltip title={t("Won't be included in export!")}>
                <FontAwesomeIcon
                  fixedWidth={true}
                  size="1x"
                  className={"text-error"}
                  style={{ marginLeft: 5 }}
                  icon={["far", "ban"]}
                />
              </Tooltip>
            </Descriptions.Item>

            <Descriptions.Item label={t("Trash/Permanently removed files")}>
              {fileStats.permanentlyRemovedFiles}
              <Tooltip title={t("Won't be included in export!")}>
                <FontAwesomeIcon
                  fixedWidth={true}
                  size="1x"
                  className={"text-error"}
                  style={{ marginLeft: 5 }}
                  icon={["far", "ban"]}
                />
              </Tooltip>
            </Descriptions.Item>
          </>
        ) : null}
      </Descriptions>
      <Descriptions bordered title={t("Storage")} style={{ marginTop: 20 }}>
        <Descriptions.Item label={t("Occupied memory")}>
          {FormatData.formatBytes(storageStats.reservedStorageSize)}
          <Tooltip title={t("All active and archived files in documents and all other modules")}>
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
      </Descriptions>
      <Descriptions bordered title={t("Directories")} style={{ marginTop: 20 }}>
        <Descriptions.Item label={t("Active directories")}>
          {dirStats.directoriesCount}
          <Tooltip title={t("All active directories (excl. ROOT and virtual/system directories)")}>
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
        <Descriptions.Item label={t("Deleted directories")}>{dirStats.deletedDirectoriesCount}</Descriptions.Item>
        <Descriptions.Item label={t("Empty directories")}>
          {dirStats.emptyDirectoriesCount}
          <Tooltip
            title={
              <div>
                <div>
                  <span>{t("Active empty directories: ")}</span>
                  <span className="mint">{dirStats.emptyActiveDirectoriesCount}</span>
                </div>
                <div>
                  <span>{t("Deleted empty directories: ")}</span>
                  <span className="mint">{dirStats.emptyDeletedDirectoriesCount}</span>
                </div>
              </div>
            }
          >
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ color: "45bfb1", marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
        {sysAdmin ? (
          <Descriptions.Item label={t("Additional empty directories (for export)")}>
            {dirStats.emptyExportDirectoriesCount}
            <Tooltip
              title={t(
                // eslint-disable-next-line max-len
                "In export a directory is considered empty if it only contains zero byte and/or permanently removed files"
              )}
            >
              <FontAwesomeIcon
                fixedWidth={true}
                size="1x"
                className={"icon-info"}
                style={{ marginLeft: 5 }}
                icon={["far", "info-circle"]}
              />
            </Tooltip>
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label={t("Linked directories")}>{dirStats.linkedDirectoriesCount}</Descriptions.Item>
      </Descriptions>
      <Descriptions bordered title={t("Directory access rights")} style={{ marginTop: 20 }}>
        <Descriptions.Item label={t("All access rights entries (incl. deleted directories)")}>
          {aclStats.accessRightsCount}
          <Tooltip
            title={
              <div>
                <div>
                  <span>{t("Rights set for users: ")}</span>
                  <span className="mint">{aclStats.userRightCount}</span>
                </div>
                <div>
                  <span>{t("Rights set for groups: ")}</span>
                  <span className="mint">{aclStats.groupRightCount}</span>
                </div>
              </div>
            }
          >
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ color: "45bfb1", marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
        <Descriptions.Item label={t("Public groups")}>{aclStats.groupsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Users/Groups that are used for rights")}>
          {aclStats.groupsUsedForAccessRightsCount + aclStats.usersUsedForAccessRightsCount}
          <Tooltip
            title={
              <div>
                <div>
                  <span>{t("Users with rights: ")}</span>
                  <span className="mint">{aclStats.usersUsedForAccessRightsCount}</span>
                </div>
                <div>
                  <span>{t("Groups with rights: ")}</span>
                  <span className="mint">{aclStats.groupsUsedForAccessRightsCount}</span>
                </div>
              </div>
            }
          >
            <FontAwesomeIcon
              fixedWidth={true}
              size="1x"
              className={"icon-info"}
              style={{ color: "45bfb1", marginLeft: 5 }}
              icon={["far", "info-circle"]}
            />
          </Tooltip>
        </Descriptions.Item>
      </Descriptions>
      <Descriptions bordered title={t("Workflow")} style={{ marginTop: 20 }}>
        <Descriptions.Item label={t("Workflow definitions")}>{wfStats.definitionsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Running workflows")}>{wfStats.runningWorkflowsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Finished workflows")}>{wfStats.finishedWorkflowsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Canceled workflows")}>{wfStats.cancelledWorkflowsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Workflows with errors")}>{wfStats.workflowsWIthErrorsCount}</Descriptions.Item>
        <Descriptions.Item label={t("Active files in workflows (stored in module documents)")}>
          {data.fileFolderStatistics.workflowFiles.linked}
        </Descriptions.Item>
        <Descriptions.Item label={t("Active files in workflows (not stored in module documents)")}>
          {data.fileFolderStatistics.workflowFiles.uploaded}
        </Descriptions.Item>
        <Descriptions.Item label={t("Last action in a workflow")}>
          {wfStats.lastWorkFlowAction ? dayjs(wfStats.lastWorkFlowAction).format("lll") : "-"}
        </Descriptions.Item>
      </Descriptions>
      {sysAdmin ? (
        <Descriptions bordered title={t("Mails")} style={{ marginTop: 20 }}>
          <Descriptions.Item label={t("Project mails")}>{mailStats.internalMailsCount}</Descriptions.Item>
          <Descriptions.Item label={t("External mails (received)")}>
            {mailStats.receivedExternalEmailsCount}
          </Descriptions.Item>
          <Descriptions.Item label={t("Count of files in external mails")}>
            {mailStats.filesInExternalReceivedEmailsCount}
          </Descriptions.Item>
          <Descriptions.Item label={t("Date of last sent internal mail")}>
            {mailStats.lastInternalMail ? dayjs(mailStats.lastInternalMail).format("lll") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Date of last received external mail")}>
            {mailStats.lastReceivedExternalMail ? dayjs(mailStats.lastReceivedExternalMail).format("lll") : "-"}
          </Descriptions.Item>
        </Descriptions>
      ) : null}
    </>
  );
};
